import Content from '../../content.js';
import SwaggerUI from 'swagger-ui';
import 'swagger-ui/dist/swagger-ui.css';

// Polyfill for Buffer (Node.js API) in browser
if (typeof window !== 'undefined' && !window.Buffer) {
  window.Buffer = require('buffer').Buffer;
}

namespace('IO.Views.Swaggers', function (exports) {
  'use strict';

  var Index = Content.extend({
    dom: {
      CONTAINER: '#swagger-ui-container',
    },

    render: function () {
      const swaggerConfig = {
        dom_id: '#swagger-ui-container',
        url: swagger_url,

        // Performance optimizations
        deepLinking: true,
        displayOperationId: false,
        defaultModelRendering: 'model',
        defaultModelsExpandDepth: -1,
        defaultModelExpandDepth: 0,
        highlightSizeThreshold: 100,
        displayRequestDuration: true,
        docExpansion: 'list',
        filter: false,
        maxDisplayedTags: 10,
        showRequestHeaders: true,
        showOperationId: false,
        validatorUrl: null,
        showExtensions: true,
        showCommonExtensions: true,
        tryItOutEnabled: true,
        requestSnippetsEnabled: true,
        syntaxHighlight: false,
        layout: 'BaseLayout',
        presets: [SwaggerUI.presets.apis],

        // Request interceptor for authentication
        requestInterceptor: (req) => {
          let timestamp = new Date().toISOString();
          const url = new URL(req.url, window.location.origin);

          req.headers['X-Auth-Token'] = authToken;
          req.headers['X-Auth-Signature'] = this._generateSignature(
            url.pathname,
            timestamp,
          );
          req.headers['Content-Type'] = 'application/json';
          req.headers.Accept = 'application/vnd.reliantsec-v1+json';
          req.headers['X-Customer'] = currentCustomer;
          req.headers['X-Timestamp'] = timestamp;
          req.headers['Access-Control-Allow'] = '*';
          return req;
        },

        // Response interceptor for error handling
        responseInterceptor: (res) => {
          if (res.status >= 400) {
            console.warn('API Error:', res.status, res.statusText);
          }
          return res;
        },
      };

      const ui = SwaggerUI(swaggerConfig);

      // Save instance globally for filter functionality
      window.ui = ui;

      // Setup UI enhancements with delay for DOM loading
      setTimeout(() => {
        this._enhanceUI();
        this._setupKeyboardShortcuts();
      }, 500);
    },

    _generateSignature: function (requestPath, timestamp) {
      const message = `${requestPath} ${timestamp}`;
      return this._calculateHMAC(message);
    },

    _calculateHMAC: function (message) {
      const hmacDigest = CryptoJS.HmacSHA256(message, secretKey);
      return hmacDigest.toString(CryptoJS.enc.Hex);
    },

    _enhanceUI() {
      // Add extra functionality after loading
      this._addCopyButtons();
      this._addSearchEnhancement();
      this._addResponsiveImprovements();
    },

    _addCopyButtons() {
      // Add copy buttons to code blocks
      const codeBlocks = document.querySelectorAll(
        '.swagger-ui .highlight-code',
      );
      codeBlocks.forEach((block) => {
        if (!block.querySelector('.copy-button')) {
          const copyBtn = document.createElement('button');
          copyBtn.className = 'copy-button';
          copyBtn.innerHTML = '📋';
          copyBtn.title = 'Copy code';

          copyBtn.addEventListener('click', () => {
            const text = block.textContent;
            navigator.clipboard.writeText(text).then(() => {
              copyBtn.innerHTML = '✅';
              setTimeout(() => {
                copyBtn.innerHTML = '📋';
              }, 2000);
            });
          });

          block.appendChild(copyBtn);
        }
      });
    },

    _addSearchEnhancement() {
      // Create custom filter field
      this._createCustomFilter();
    },

    _createCustomFilter() {
      // Create filter immediately to avoid late loading
      const filterContainer = document.createElement('div');
      filterContainer.id = 'custom-swagger-filter';

      // Add placeholder while loading
      const placeholder = document.createElement('div');
      placeholder.className = 'filter-placeholder';
      document.body.appendChild(placeholder);

      // Wait for information container to load
      setTimeout(() => {
        const infoContainer = document.querySelector('.swagger-ui .info');
        if (!infoContainer) {
          // If not found, remove placeholder and try again
          placeholder.remove();
          setTimeout(() => this._createCustomFilter(), 200);
          return;
        }

        // Remove placeholder
        placeholder.remove();

        // Create filter input
        const filterInput = document.createElement('input');
        filterInput.type = 'text';
        filterInput.placeholder = 'Search endpoints...';

        // Add keyboard shortcuts hint
        const shortcutsHint = document.createElement('div');
        shortcutsHint.className = 'shortcuts-hint';
        shortcutsHint.innerHTML =
          '<kbd>Ctrl+F</kbd> to focus • <kbd>Esc</kbd> to clear';

        // Add search event
        let filterTimeout;
        filterInput.addEventListener('input', (e) => {
          const searchTerm = e.target.value.toLowerCase();

          // Debounce to avoid multiple executions
          clearTimeout(filterTimeout);
          filterTimeout = setTimeout(() => {
            this._filterEndpoints(searchTerm);
          }, 100);
        });

        // Add to container
        filterContainer.appendChild(filterInput);
        filterContainer.appendChild(shortcutsHint);
        infoContainer.parentNode.insertBefore(
          filterContainer,
          infoContainer.nextSibling,
        );

        // Fade in filter
        setTimeout(() => {
          filterContainer.style.opacity = '1';
        }, 50);
      }, 200);
    },

    _filterEndpoints(searchTerm) {
      // Custom filter for endpoints - searches specifically in URLs
      const endpoints = document.querySelectorAll('.swagger-ui .opblock');

      endpoints.forEach((endpoint) => {
        if (searchTerm === '') {
          // If no search term, show all endpoints
          endpoint.style.display = 'block';
        }

        // Search in different endpoint elements
        const pathElement = endpoint.querySelector('.opblock-summary-path');
        const methodElement = endpoint.querySelector('.opblock-summary-method');
        const descriptionElement = endpoint.querySelector(
          '.opblock-summary-description',
        );
        const summaryElement = endpoint.querySelector('.opblock-summary');

        let shouldShow = false;

        // Search specifically in URL/path
        if (pathElement) {
          const pathText = pathElement.textContent.toLowerCase();
          // Remove leading slash to facilitate search
          const cleanPathText = pathText.replace(/^\//, '');
          if (
            pathText.includes(searchTerm) ||
            cleanPathText.includes(searchTerm)
          ) {
            shouldShow = true;
          }
        }

        // Search in HTTP method
        if (!shouldShow && methodElement) {
          const methodText = methodElement.textContent.toLowerCase();
          if (methodText.includes(searchTerm)) {
            shouldShow = true;
          }
        }

        // Search in description
        if (!shouldShow && descriptionElement) {
          const descText = descriptionElement.textContent.toLowerCase();
          if (descText.includes(searchTerm)) {
            shouldShow = true;
          }
        }

        // Search in entire summary as fallback
        if (!shouldShow && summaryElement) {
          const summaryText = summaryElement.textContent.toLowerCase();
          if (summaryText.includes(searchTerm)) {
            shouldShow = true;
          }
        }

        endpoint.style.display = shouldShow ? 'block' : 'none';
      });

      // Hide empty sections (only if there's a search term)
      if (searchTerm !== '') {
        this._hideEmptySections();
      } else {
        // If no search, show all sections
        this._showAllSections();
      }
    },

    _hideEmptySections() {
      // Find all sections (tags) and check if they have visible endpoints
      const sections = document.querySelectorAll('.swagger-ui .opblock-tag');

      sections.forEach((section) => {
        const sectionEndpoints = section.querySelectorAll('.opblock');
        const visibleEndpoints = Array.from(sectionEndpoints).filter(
          (endpoint) => endpoint.style.display !== 'none',
        );

        // If no visible endpoints, hide entire section
        if (visibleEndpoints.length === 0) {
          section.style.display = 'none';
        } else {
          // Show the section
          section.style.display = 'block';
        }
      });
    },

    _showAllSections() {
      // Show all sections when there's no search term
      const sections = document.querySelectorAll('.swagger-ui .opblock-tag');
      sections.forEach((section) => {
        section.style.display = 'flex';
      });
    },

    _addResponsiveImprovements() {
      // Improve responsiveness
      const wrapper = document.querySelector('.swagger-ui .wrapper');
      if (wrapper) {
        wrapper.style.margin = '0 auto';
        wrapper.style.padding = '20px';
      }
    },

    _setupKeyboardShortcuts() {
      // Add keyboard shortcuts
      const handleKeydown = (e) => {
        // Ctrl/Cmd + F to focus search (only when on Swagger page)
        if ((e.ctrlKey || e.metaKey) && e.key === 'f') {
          const filterInput = document.querySelector(
            '#custom-swagger-filter input',
          );
          if (filterInput) {
            e.preventDefault();
            filterInput.focus();
            filterInput.select();
          }
        }

        // ESC to clear search
        if (e.key === 'Escape') {
          const filterInput = document.querySelector(
            '#custom-swagger-filter input',
          );
          if (filterInput && filterInput.value) {
            filterInput.value = '';
            this._filterEndpoints('');
            filterInput.blur();
          }
        }
      };

      document.addEventListener('keydown', handleKeydown);
    },
  });

  exports.Index = Index;
});
